import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';

export default function AdminTools() {
  const { user } = useAuth();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    url: '',
    usage_instructions: '',
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);

  // โหลดข้อมูลเครื่องมือทั้งหมด
  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('tools')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) throw error;
      setTools(data || []);
    } catch (error) {
      console.error('Error fetching tools:', error);
      setError('ไม่สามารถโหลดข้อมูลเครื่องมือได้');
    } finally {
      setLoading(false);
    }
  };

  // จัดการการเปลี่ยนแปลงข้อมูลในฟอร์ม
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // จัดการการเลือกไฟล์รูปภาพ
  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // ตรวจสอบประเภทไฟล์
    if (!file.type.startsWith('image/')) {
      setError('กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น');
      return;
    }

    // ตรวจสอบขนาดไฟล์ (ไม่เกิน 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('ขนาดไฟล์ต้องไม่เกิน 2MB');
      return;
    }

    setThumbnail(file);
    
    // สร้าง URL สำหรับแสดงตัวอย่างรูปภาพ
    const previewUrl = URL.createObjectURL(file);
    setThumbnailPreview(previewUrl);
  };

  // อัปโหลดรูปภาพไปยัง Supabase Storage
  const uploadThumbnail = async (file) => {
    try {
      setUploading(true);
      
      // สร้างชื่อไฟล์ที่ไม่ซ้ำกัน
      const fileExt = file.name.split('.').pop();
      const fileName = `tool-${Date.now()}.${fileExt}`;
      
      // อัปโหลดไฟล์ไปยัง Supabase Storage
      const { data, error } = await supabase.storage
        .from('payment_slips')
        .upload(fileName, file);
      
      if (error) throw error;
      
      // สร้าง URL สำหรับเข้าถึงรูปภาพ
      const { data: urlData } = supabase.storage
        .from('payment_slips')
        .getPublicUrl(fileName);
      
      console.log('Upload successful:', urlData);
      return urlData.publicUrl;
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      setError(`อัปโหลดรูปภาพไม่สำเร็จ: ${error.message}`);
      throw error;
    } finally {
      setUploading(false);
    }
  };


  // บันทึกข้อมูลเครื่องมือ
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError(null);
      setSuccess(null);
      
      // ตรวจสอบข้อมูลที่จำเป็น
      if (!formData.name || !formData.description || !formData.url) {
        setError('กรุณากรอกข้อมูลที่จำเป็น (ชื่อ, รายละเอียด, URL)');
        return;
      }
      
      let thumbnailUrl = null;
      
      // อัปโหลดรูปภาพถ้ามีการเลือกไฟล์
      if (thumbnail) {
        try {
          thumbnailUrl = await uploadThumbnail(thumbnail);
        } catch (uploadError) {
          // ถ้าอัปโหลดไม่สำเร็จแต่ยังต้องการบันทึกข้อมูลอื่นๆ
          console.error('Upload failed but continuing with save:', uploadError);
        }
      }
      
      if (editingId) {
        // อัปเดตข้อมูลเครื่องมือที่มีอยู่
        const updateData = { ...formData };
        if (thumbnailUrl) {
          updateData.thumbnail = thumbnailUrl;
        }
        
        const { error } = await supabase
          .from('tools')
          .update(updateData)
          .eq('id', editingId);
        
        if (error) {
          console.error('Update error:', error);
          throw error;
        }
        setSuccess('อัปเดตข้อมูลเครื่องมือเรียบร้อยแล้ว');
      } else {
        // เพิ่มข้อมูลเครื่องมือใหม่
        const newTool = {
          ...formData
        };
        
        if (thumbnailUrl) {
          newTool.thumbnail = thumbnailUrl;
        }
        
        const { error } = await supabase
          .from('tools')
          .insert(newTool);
        
        if (error) {
          console.error('Insert error:', error);
          throw error;
        }
        setSuccess('เพิ่มข้อมูลเครื่องมือเรียบร้อยแล้ว');
      }
      
      // รีเซ็ตฟอร์มและโหลดข้อมูลใหม่
      resetForm();
      fetchTools();
    } catch (error) {
      console.error('Error saving tool:', error);
      setError(`เกิดข้อผิดพลาดในการบันทึกข้อมูล: ${error.message}`);
    }
  };

  // โหลดข้อมูลเครื่องมือเพื่อแก้ไข
  const handleEdit = (tool) => {
    setFormData({
      name: tool.name,
      description: tool.description,
      url: tool.url,
      usage_instructions: tool.usage_instructions || '',
    });
    setEditingId(tool.id);
    setThumbnailPreview(tool.thumbnail);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ลบข้อมูลเครื่องมือ
  const handleDelete = async (id) => {
    if (!window.confirm('คุณต้องการลบเครื่องมือนี้ใช่หรือไม่?')) return;
    
    try {
      const { error } = await supabase
        .from('tools')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      setSuccess('ลบข้อมูลเครื่องมือเรียบร้อยแล้ว');
      fetchTools();
    } catch (error) {
      console.error('Error deleting tool:', error);
      setError('เกิดข้อผิดพลาดในการลบข้อมูล');
    }
  };
  
  // อัปเดตลำดับ
  const handleUpdateOrderIndex = async (toolId, newOrderIndex) => {
    try {
      // ตรวจสอบว่าค่าที่ใส่เป็นตัวเลขที่ถูกต้อง
      const orderIndex = parseInt(newOrderIndex);
      if (isNaN(orderIndex) || orderIndex < 0) {
        setError('ลำดับต้องเป็นตัวเลขที่มากกว่าหรือเท่ากับ 0');
        return;
      }
      
      // อัปเดตลำดับในฐานข้อมูล
      const { error } = await supabase
        .from('tools')
        .update({ order_index: orderIndex })
        .eq('id', toolId);
      
      if (error) throw error;
      
      setSuccess('อัปเดตลำดับเรียบร้อยแล้ว');
      fetchTools();
    } catch (error) {
      console.error('Error updating order index:', error);
      setError('เกิดข้อผิดพลาดในการอัปเดตลำดับ');
    }
  };

  // รีเซ็ตฟอร์ม
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      url: '',
      usage_instructions: '',
    });
    setThumbnail(null);
    setThumbnailPreview(null);
    setEditingId(null);
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>จัดการเครื่องมือ - Admin</title>
      </Helmet>

      <main className="flex-grow container mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-8 text-center">จัดการเครื่องมือ</h1>

        {/* แสดงข้อความแจ้งเตือน */}
        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-300 p-4 rounded-lg mb-6">
            <p>{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-500/20 border border-green-500 text-green-300 p-4 rounded-lg mb-6">
            <p>{success}</p>
          </div>
        )}

        {/* ฟอร์มเพิ่ม/แก้ไขข้อมูลเครื่องมือ */}
        <div className="bg-gray-800 rounded-xl shadow-lg p-6 mb-10">
          <h2 className="text-xl font-bold mb-4">{editingId ? 'แก้ไขเครื่องมือ' : 'เพิ่มเครื่องมือใหม่'}</h2>
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-gray-300 mb-2">ชื่อเครื่องมือ *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                  required
                />
              </div>
              
              <div>
                <label className="block text-gray-300 mb-2">URL *</label>
                <input
                  type="text"
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                  required
                />
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-300 mb-2">รายละเอียด *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                required
              ></textarea>
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-300 mb-2">วิธีใช้งาน</label>
              <textarea
                name="usage_instructions"
                value={formData.usage_instructions}
                onChange={handleChange}
                rows="4"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
              ></textarea>
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-300 mb-2">รูปภาพ Thumbnail</label>
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                  className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                />
                
                {thumbnailPreview && (
                  <div className="flex-shrink-0">
                    <img
                      src={thumbnailPreview}
                      alt="Thumbnail preview"
                      className="w-16 h-16 rounded-full object-cover border-2 border-purple-500"
                    />
                  </div>
                )}
              </div>
              <p className="text-gray-400 text-sm mt-1">ขนาดไฟล์ไม่เกิน 2MB</p>
            </div>
            
            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={uploading}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white font-medium transition-colors disabled:opacity-50"
              >
                {uploading ? 'กำลังอัปโหลด...' : editingId ? 'อัปเดต' : 'บันทึก'}
              </button>
              
              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg text-white font-medium transition-colors"
                >
                  ยกเลิก
                </button>
              )}
            </div>
          </form>
        </div>

        {/* แสดงรายการเครื่องมือ */}
        <div className="bg-gray-800 rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold mb-4">รายการเครื่องมือทั้งหมด</h2>
          
          {loading ? (
            <div className="flex justify-center items-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500"></div>
            </div>
          ) : tools.length === 0 ? (
            <p className="text-gray-400 text-center py-6">ยังไม่มีเครื่องมือในระบบ</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="px-4 py-3 text-left">Thumbnail</th>
                    <th className="px-4 py-3 text-left">ชื่อ</th>
                    <th className="px-4 py-3 text-left">URL</th>
                    <th className="px-4 py-3 text-left">รายละเอียด</th>
                    <th className="px-4 py-3 text-center">ลำดับ</th>
                    <th className="px-4 py-3 text-right">จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {tools.map((tool, index) => (
                    <tr key={tool.id} className="border-b border-gray-700">
                      <td className="px-4 py-3">
                        {tool.thumbnail ? (
                          <img
                            src={tool.thumbnail}
                            alt={tool.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center">
                            <span className="text-xs">ไม่มี</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">{tool.name}</td>
                      <td className="px-4 py-3 max-w-[200px] truncate">
                        <a
                          href={tool.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-400 hover:text-purple-300"
                        >
                          {tool.url}
                        </a>
                      </td>
                      <td className="px-4 py-3 max-w-[300px] truncate">{tool.description}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center items-center space-x-2">
                          <input
                            type="number"
                            min="0"
                            value={tool.order_index || 0}
                            onChange={(e) => handleUpdateOrderIndex(tool.id, e.target.value)}
                            className="w-16 bg-gray-700 border border-gray-600 rounded text-center text-white py-1 px-2"
                            title="กรอกลำดับ"
                          />
                          <span className="text-xs text-gray-400">ลำดับ</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleEdit(tool)}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-white text-sm mr-2"
                        >
                          แก้ไข
                        </button>
                        <button
                          onClick={() => handleDelete(tool.id)}
                          className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-white text-sm"
                        >
                          ลบ
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
